import React from 'react';

// ==================== CORES DA MARCA ====================
const CORES = {
  azulEscuro: '#1F3D7A',
  azulMedio: '#0D5DFC',
  ciano: '#00BEFF',
};

/**
 * Componente de Loading reutilizável com animação de bouncing dots
 * 
 * Props:
 * - texto: string (opcional) - Texto a ser exibido abaixo dos dots (default: "Carregando...")
 * - tamanho: 'sm' | 'md' | 'lg' (opcional) - Tamanho dos dots (default: 'md')
 * - fullScreen: boolean (opcional) - Se true, ocupa a tela inteira (default: true)
 * - semFundo: boolean (opcional) - Se true, não mostra o card de fundo (default: false)
 */
const Loading = ({ 
  texto = "Carregando...", 
  tamanho = "md",
  fullScreen = true,
  semFundo = false 
}) => {
  
  // Configurações de tamanho
  const tamanhos = {
    sm: { dot: 10, gap: 6, bounce: 12 },
    md: { dot: 14, gap: 8, bounce: 18 },
    lg: { dot: 18, gap: 10, bounce: 24 }
  };
  
  const config = tamanhos[tamanho] || tamanhos.md;

  // Estilos inline para as animações (necessário para keyframes dinâmicos)
  const styles = `
    @keyframes bounceLoader {
      0%, 100% {
        transform: translateY(0);
        opacity: 1;
      }
      50% {
        transform: translateY(-${config.bounce}px);
        opacity: 0.7;
      }
    }
    
    @keyframes pulseGlow {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(13, 93, 252, 0.4);
      }
      50% {
        box-shadow: 0 0 20px 5px rgba(13, 93, 252, 0.2);
      }
    }
    
    @keyframes shimmer {
      0% {
        background-position: -200% center;
      }
      100% {
        background-position: 200% center;
      }
    }
  `;

  const DotsContent = () => (
    <div className="flex flex-col items-center">
      {/* Bouncing Dots */}
      <div 
        className="flex items-end justify-center"
        style={{ gap: `${config.gap}px`, height: `${config.dot + config.bounce + 5}px` }}
      >
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            style={{
              width: `${config.dot}px`,
              height: `${config.dot}px`,
              borderRadius: '50%',
              background: index === 0 
                ? CORES.azulEscuro 
                : index === 1 
                  ? CORES.azulMedio 
                  : CORES.ciano,
              animation: `bounceLoader 1.4s ease-in-out infinite`,
              animationDelay: `${index * 0.16}s`,
              boxShadow: `0 2px 8px ${index === 0 
                ? 'rgba(31, 61, 122, 0.4)' 
                : index === 1 
                  ? 'rgba(13, 93, 252, 0.4)' 
                  : 'rgba(0, 190, 255, 0.4)'}`
            }}
          />
        ))}
      </div>
      
      {/* Texto com efeito shimmer */}
      {texto && (
        <p 
          className="mt-4 font-medium text-sm"
          style={{
            background: `linear-gradient(90deg, ${CORES.azulEscuro} 0%, ${CORES.azulMedio} 50%, ${CORES.ciano} 100%)`,
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'shimmer 2s linear infinite'
          }}
        >
          {texto}
        </p>
      )}
    </div>
  );

  // Versão sem fundo (apenas os dots)
  if (semFundo) {
    return (
      <>
        <style>{styles}</style>
        <div className={fullScreen ? "min-h-screen flex items-center justify-center" : "flex items-center justify-center py-8"}>
          <DotsContent />
        </div>
      </>
    );
  }

  // Versão com card glassmorphism
  return (
    <>
      <style>{styles}</style>
      <div 
        className={`${fullScreen ? 'min-h-screen' : 'min-h-[200px]'} flex items-center justify-center`}
        style={{
          background: fullScreen 
            ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)' 
            : 'transparent'
        }}
      >
        <div 
          className="px-10 py-8 flex flex-col items-center"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.5) inset'
          }}
        >
          <DotsContent />
        </div>
      </div>
    </>
  );
};

export default Loading;